import { useState } from "react";
import { monthsText } from "../../lib/duration";
import { api } from "../../api/client";
import { downloadFile } from "../../api/download";

/**
 * Deliverables beside the integrated report: prepare the project's inputs from a CMDB and a UCDI
 * export, the data confirmation workbook (download, then upload the returned file), the PQC
 * compliance matrix and the executive deck. Everything is built by this QRAM server (offline).
 */
/** "Firewall Appliance = Security Appliance" lines → { source: QRAM type }. */
export function parseTypeMap(text: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const line of text.split("\n")) {
    const i = line.indexOf("=");
    if (i < 0) continue;
    const k = line.slice(0, i).trim(), v = line.slice(i + 1).trim();
    if (k && v) out[k] = v;
  }
  return out;
}

type Role = "cmdb" | "ucdi_unified" | "ucdi_cbom" | "ucdi_owners" | "estimates";
interface Issue { severity: "error" | "warning" | "info"; kind: string; message: string; rows?: string[] }
interface Preview {
  mapping: { cmdb: Record<string, string>; ucdi: Record<string, string>; defaults?: Record<string, string>; type_map?: Record<string, string>; date_order?: "DMY" | "MDY" };
  columns: { cmdb: string[]; ucdi: string[] };
  fields: { cmdb: string[]; ucdi: string[] };
  counts: Record<string, number>;
  issues: Issue[];
  ha_groups: Array<{ group: string; members: string[] }>;
  estimates: Array<{ model: string; assets: number; time_for_migration: string | null; migration_cost_range: string | null; basis: string }>;
  preflight: { total_rows: number; failed: number; errors: Array<{ row: number; name: string | null; errors: string[] }> } | null;
}
interface ImportResult { assets: { imported: number; updated: number; failed: number }; crypto: Array<{ file: string; imported: number; linked: number; unlinked: number }> }
interface ConfirmPreview { changes: Array<{ sheet: string; row_key: string; field: string; old_value: string | null; new_value: string | null; status: string }>; errors: string[]; warnings: string[] }
interface Settings { reassessment_cadence: string | null; reassessment_owner: string | null }

const PREP_ROLES: Array<{ role: Role; label: string; hint: string; required?: boolean }> = [
  { role: "cmdb", label: "CMDB export", hint: "CSV or XLSX, one row per asset", required: true },
  { role: "ucdi_unified", label: "UCDI unified CSV", hint: "the cryptography export (artifact rows)" },
  { role: "ucdi_cbom", label: "UCDI CBOM", hint: "CBOM CSV or CycloneDX JSON" },
  { role: "ucdi_owners", label: "UCDI owner list", hint: "asset, owner email (optional)" },
  { role: "estimates", label: "Migration estimates by model", hint: "model, migration months, cost band (optional)" },
];
const CADENCES: Array<{ v: string; label: string }> = [
  { v: "", label: "Not recorded" }, { v: "quarterly", label: "Quarterly" }, { v: "half_yearly", label: "Every six months" },
  { v: "annual", label: "Annual" }, { v: "biennial", label: "Every two years" },
];

const toBase64 = (f: File) => new Promise<string>((ok, fail) => {
  const r = new FileReader();
  r.onload = () => ok(String(r.result).split(",")[1] ?? "");
  r.onerror = () => fail(r.error);
  r.readAsDataURL(f);
});

const download = async (path: string, fallback: string, body?: unknown): Promise<void> => { await downloadFile(path, fallback, body); };

const small = "text-[11px] text-ink-700 hover:text-accent border border-ink-200 rounded-sm px-2 py-1 disabled:opacity-50";
const msg = (e: unknown) => (e instanceof Error ? e.message : String(e));

function PrepareInputs({ projectId }: { projectId: string }) {
  const [files, setFiles] = useState<Partial<Record<Role, File>>>({});
  const [mapping, setMapping] = useState<Preview["mapping"] | null>(null);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const step = result ? 4 : preview ? 3 : 1;

  const payload = async () => ({
    files: await Promise.all((Object.entries(files) as Array<[Role, File]>).map(async ([role, f]) => ({ role, name: f.name, data: await toBase64(f) }))),
    ...(mapping ? { mapping } : {}),
  });
  const run = async (fn: () => Promise<void>) => { setBusy(true); setError(null); try { await fn(); } catch (e) { setError(msg(e)); } finally { setBusy(false); } };
  const doPreview = () => run(async () => {
    const p = await api.post<Preview>(`/projects/${projectId}/deliverables/prepare-inputs/preview`, await payload());
    setPreview(p); setMapping(p.mapping); setResult(null);
  });
  const doImport = () => run(async () => {
    setResult(await api.post<ImportResult>(`/projects/${projectId}/deliverables/prepare-inputs/import`, { ...(await payload()), mode: "upsert" }));
  });
  const blocking = preview?.issues.some((i) => i.severity === "error" && i.kind !== "conflict") ?? false;

  return (
    <div className="mt-2 space-y-2 text-[11px] text-ink-700" data-testid="prepare-inputs">
      <ol className="flex gap-3 text-ink-500"><li className={step === 1 ? "font-semibold text-ink-900" : ""}>1 Upload</li><li className={step === 3 && mapping ? "font-semibold text-ink-900" : ""}>2 Map</li><li className={step === 3 ? "font-semibold text-ink-900" : ""}>3 Preview</li><li className={step === 4 ? "font-semibold text-ink-900" : ""}>4 Import</li></ol>
      {PREP_ROLES.map((r) => (
        <label key={r.role} className="block">
          <span className="font-semibold">{r.label}</span> <span className="text-ink-400">{r.hint}</span>
          <input type="file" accept=".csv,.tsv,.xlsx,.json" className="block mt-0.5" data-testid={`prepare-file-${r.role}`}
            onChange={(e) => { const f = e.target.files?.[0]; setFiles((x) => ({ ...x, [r.role]: f })); setPreview(null); setResult(null); }} />
        </label>
      ))}
      <button type="button" className={small} onClick={doPreview} disabled={busy || !files.cmdb} data-testid="prepare-preview">{busy && !preview ? "Reading…" : preview ? "Refresh preview" : "Map and preview"}</button>
      {preview && mapping && (
        <div className="space-y-2" data-testid="prepare-preview-result">
          <details>
            <summary className="cursor-pointer font-semibold">Column mapping (saved with the project for next time)</summary>
            <table className="mt-1"><tbody>
              {preview.fields.cmdb.map((f) => (
                <tr key={f}><td className="pr-2 text-ink-500">{f}</td><td>
                  <select value={mapping.cmdb[f] ?? ""} data-testid={`prepare-map-${f}`} className="border border-ink-200 rounded-sm"
                    onChange={(e) => setMapping((m) => m && ({ ...m, cmdb: { ...m.cmdb, [f]: e.target.value } }))}>
                    <option value="">(not mapped)</option>
                    {preview.columns.cmdb.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </td></tr>
              ))}
            </tbody></table>
            <label className="block mt-2 text-ink-500">Type mapping: one per line, CMDB or UCDI type = QRAM asset type. The CMDB type is used first; UCDI's only when the CMDB has none.
              <textarea data-testid="prepare-type-map" rows={3} className="block w-full border border-ink-200 rounded-sm font-mono"
                defaultValue={Object.entries(mapping.type_map ?? {}).map(([k, v]) => `${k} = ${v}`).join("\n")}
                onBlur={(e) => { const tm = parseTypeMap(e.target.value); setMapping((m) => m && ({ ...m, type_map: tm })); }} />
            </label>
            <label className="block mt-2 text-ink-500">Numeric dates in the CMDB export
              <select value={mapping.date_order ?? "DMY"} data-testid="prepare-date-order" className="ml-2 border border-ink-200 rounded-sm"
                onChange={(e) => setMapping((m) => m && ({ ...m, date_order: e.target.value as "DMY" | "MDY" }))}>
                <option value="DMY">DD/MM/YYYY</option>
                <option value="MDY">MM/DD/YYYY</option>
              </select>
            </label>
          </details>
          <p data-testid="prepare-counts">
            {preview.counts.cmdb_rows} CMDB rows · {preview.counts.matched} joined to UCDI · {preview.counts.unmatched_cmdb} without UCDI evidence · {preview.counts.unmatched_ucdi} UCDI assets not in the CMDB · {preview.counts.conflicts} conflicts · owners from UCDI {preview.counts.owners_from_ucdi} · {preview.counts.ha_groups} HA groups · {preview.counts.models} device models
          </p>
          {preview.issues.length > 0 && (
            <ul className="space-y-1" data-testid="prepare-issues">
              {preview.issues.map((i) => (
                <li key={i.kind + i.message} className={i.severity === "error" ? "text-red-700" : i.severity === "warning" ? "text-amber-700" : "text-ink-500"}>
                  {i.message}{i.rows?.length ? <details className="inline"><summary className="inline cursor-pointer"> show</summary><span className="block pl-3 text-ink-500">{i.rows.slice(0, 40).join("; ")}{i.rows.length > 40 ? " …" : ""}</span></details> : null}
                </li>
              ))}
            </ul>
          )}
          {preview.preflight && preview.preflight.failed > 0 && (
            <p className="text-red-700" data-testid="prepare-preflight">{preview.preflight.failed} of {preview.preflight.total_rows} rows would fail the asset import: {preview.preflight.errors.slice(0, 3).map((e) => `${e.name ?? `row ${e.row}`}: ${e.errors.join(", ")}`).join("; ")}</p>
          )}
          <details><summary className="cursor-pointer">Migration estimates by device model ({preview.estimates.length})</summary>
            <ul className="pl-3">{preview.estimates.map((e) => <li key={e.model}>{e.model} · {e.assets} assets · {e.time_for_migration ? monthsText(Number(e.time_for_migration)) : "—"} · {e.migration_cost_range ?? "—"} <span className="text-ink-400">({e.basis})</span></li>)}</ul>
          </details>
          <button type="button" className={small} onClick={doImport} disabled={busy || blocking} data-testid="prepare-import">{busy ? "Importing…" : "Import assets and UCDI cryptography"}</button>
        </div>
      )}
      {result && (
        <p data-testid="prepare-result">Assets: {result.assets.imported} added, {result.assets.updated} updated, {result.assets.failed} failed. {result.crypto.map((c) => `${c.file}: ${c.imported} items, ${c.linked} linked, ${c.unlinked} unlinked`).join("; ")}. Re-run the assessment to use them.</p>
      )}
      {error && <p className="text-red-700" role="alert" data-testid="prepare-error">{error}</p>}
    </div>
  );
}

function Confirmation({ projectId, qsetfFiles }: { projectId: string; qsetfFiles: File[] }) {
  const [returned, setReturned] = useState<File | null>(null);
  const [preview, setPreview] = useState<ConfirmPreview | null>(null);
  const [applied, setApplied] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async (fn: () => Promise<void>) => { setBusy(true); setError(null); try { await fn(); } catch (e) { setError(msg(e)); } finally { setBusy(false); } };
  const get = () => run(async () => {
    const path = `/projects/${projectId}/deliverables/confirmation.xlsx`;
    if (qsetfFiles.length) await download(path, "Data_Confirmation.xlsx", { qsetf: await Promise.all(qsetfFiles.map(async (f) => ({ name: f.name, data: await toBase64(f) }))) });
    else await download(path, "Data_Confirmation.xlsx");
  });
  const body = async () => ({ name: returned!.name, data: await toBase64(returned!) });
  const check = () => run(async () => { setApplied(null); setPreview(await api.post<ConfirmPreview>(`/projects/${projectId}/deliverables/confirmation/preview`, await body())); });
  const apply = () => run(async () => {
    const r = await api.post<{ recorded: number; assets: { updated: number; failed: number; errors: string[] }; warnings: string[] }>(`/projects/${projectId}/deliverables/confirmation/apply`, await body());
    setApplied(`Applied ${r.recorded} values (recorded in the audit trail); ${r.assets.updated} assets updated${r.assets.failed ? `, ${r.assets.failed} failed` : ""}. Re-run the assessment to use them.`);
  });
  return (
    <div className="mt-2 space-y-2 text-[11px] text-ink-700" data-testid="confirmation">
      <button type="button" className={small} onClick={get} disabled={busy} data-testid="confirmation-download">Download workbook{qsetfFiles.length ? " (with the QS-ETSF files above)" : ""}</button>
      <label className="block"><span className="font-semibold">Returned workbook</span>
        <input type="file" accept=".xlsx" className="block mt-0.5" data-testid="confirmation-file" onChange={(e) => { setReturned(e.target.files?.[0] ?? null); setPreview(null); setApplied(null); }} />
      </label>
      <button type="button" className={small} onClick={check} disabled={busy || !returned} data-testid="confirmation-preview">Check changes</button>
      {preview && (
        <div data-testid="confirmation-preview-result">
          <p>{preview.changes.length} confirmed or corrected values to apply{preview.errors.length ? `, ${preview.errors.length} errors` : ""}.</p>
          {preview.errors.length > 0 && <ul className="list-disc pl-4 text-red-700">{preview.errors.slice(0, 20).map((e) => <li key={e}>{e}</li>)}</ul>}
          {preview.changes.length > 0 && (
            <table className="mt-1"><tbody>{preview.changes.slice(0, 30).map((c) => (
              <tr key={`${c.sheet}|${c.row_key}|${c.field}`}><td className="pr-2">{c.sheet}</td><td className="pr-2">{c.row_key}</td><td className="pr-2">{c.field}</td><td className="pr-2 text-ink-400">{c.old_value ?? "—"}</td><td>→ {c.new_value ?? "—"}</td></tr>
            ))}</tbody></table>
          )}
          <button type="button" className={`${small} mt-1`} onClick={apply} disabled={busy || preview.errors.length > 0 || preview.changes.length === 0} data-testid="confirmation-apply">Apply to the project</button>
        </div>
      )}
      {applied && <p data-testid="confirmation-applied">{applied}</p>}
      {error && <p className="text-red-700" role="alert" data-testid="confirmation-error">{error}</p>}
    </div>
  );
}

function ComplianceSettings({ projectId }: { projectId: string }) {
  const [s, setS] = useState<Settings | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = () => { if (!s) api.get<Settings>(`/projects/${projectId}/deliverables/settings`).then(setS).catch((e) => setError(msg(e))); };
  const save = (cadence: string) => api.put<Settings>(`/projects/${projectId}/deliverables/settings`, { reassessment_cadence: cadence || null }).then(setS).catch((e) => setError(msg(e)));
  return (
    <label className="flex items-center gap-1.5 text-[11px] text-ink-700" onFocus={load} onMouseEnter={load}>
      SEBI CSCRF reassessment cadence
      <select value={s?.reassessment_cadence ?? ""} onChange={(e) => save(e.target.value)} className="border border-ink-200 rounded-sm" data-testid="compliance-cadence">
        {CADENCES.map((c) => <option key={c.v} value={c.v}>{c.label}</option>)}
      </select>
      {error && <span className="text-red-700" role="alert">{error}</span>}
    </label>
  );
}

export function IntegratedDeliverables({ projectId, qsetfFiles = [] }: { projectId: string; qsetfFiles?: File[] }) {
  const [panel, setPanel] = useState<"prepare" | "confirm" | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const get = async (key: string, path: string, fallback: string) => {
    setBusy(key); setError(null);
    try { await download(path, fallback); } catch (e) { setError(msg(e)); } finally { setBusy(null); }
  };
  return (
    <div className="mt-2 border-t border-ink-100 pt-2" data-testid="integrated-deliverables">
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" className={small} aria-expanded={panel === "prepare"} onClick={() => setPanel((p) => (p === "prepare" ? null : "prepare"))} data-testid="deliverables-prepare">Prepare inputs</button>
        <button type="button" className={small} aria-expanded={panel === "confirm"} onClick={() => setPanel((p) => (p === "confirm" ? null : "confirm"))} data-testid="deliverables-confirmation">Data confirmation workbook</button>
        <button type="button" className={small} disabled={busy !== null} onClick={() => get("matrix", `/projects/${projectId}/deliverables/compliance-matrix.xlsx`, "PQC_Compliance_Matrix.xlsx")} data-testid="deliverables-matrix">{busy === "matrix" ? "Building…" : "Compliance matrix"}</button>
        <button type="button" className={small} disabled={busy !== null} onClick={() => get("deck", `/projects/${projectId}/deliverables/executive-deck.pptx`, "Executive_Briefing.pptx")} data-testid="deliverables-deck">{busy === "deck" ? "Building…" : "Executive deck"}</button>
      </div>
      <div className="mt-1"><ComplianceSettings projectId={projectId} /></div>
      {panel === "prepare" && <PrepareInputs projectId={projectId} />}
      {panel === "confirm" && <Confirmation projectId={projectId} qsetfFiles={qsetfFiles} />}
      {error && <p className="text-[11px] text-red-700" role="alert" data-testid="deliverables-error">{error}</p>}
    </div>
  );
}
